import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from 'react';

export interface SignaturePadProps {
  onChange: (hasStroke: boolean) => void;
}

export interface SignaturePadHandle {
  clear: () => void;
  /** A `data:image/png;base64,...` URL, or `null` if there was no stroke. */
  exportPng: () => string | null;
}

/** CSS pixels the canvas occupies, before device-pixel-ratio scaling. */
const DISPLAY_WIDTH = 800;
const DISPLAY_HEIGHT = 300;
/** Clamped so `DISPLAY_WIDTH/HEIGHT * MAX_DPR` never exceeds the server's 1600x800 max (`signature-png-validator.ts`). */
const MAX_DPR = 2;
/** Accumulated pointer distance, in canvas pixels, before a tap counts as a real stroke. */
const STROKE_THRESHOLD_PX = 20;

interface Point {
  x: number;
  y: number;
}

/**
 * Canvas + native `PointerEvent` signature capture, no third-party library
 * (`proposal.md` decision 1). Complements, but never replaces, the
 * server-side ≥1% non-background-pixel validation
 * (`signature-png-validator.ts`) — this component's own stroke-distance
 * threshold only avoids counting an accidental tap as a signature.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad({ onChange }, ref): JSX.Element {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef<Point | null>(null);
    const totalDistanceRef = useRef(0);
    const hasStrokeRef = useRef(false);

    useEffect(() => {
      const canvas = canvasRef.current;

      if (canvas === null) return;

      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);

      canvas.width = Math.round(DISPLAY_WIDTH * dpr);
      canvas.height = Math.round(DISPLAY_HEIGHT * dpr);

      const context = canvas.getContext('2d');

      if (context !== null) {
        context.scale(dpr, dpr);
        context.lineWidth = 2;
        context.lineCap = 'round';
        context.strokeStyle = '#0f172a';
      }
    }, []);

    function pointFromEvent(event: ReactPointerEvent<HTMLCanvasElement> | PointerEvent): Point {
      const rect = canvasRef.current?.getBoundingClientRect();

      // The canvas is drawn in DISPLAY_WIDTH x DISPLAY_HEIGHT units but may be shown narrower (phone, modal).
      const scaleX = rect !== undefined && rect.width > 0 ? DISPLAY_WIDTH / rect.width : 1;
      const scaleY = rect !== undefined && rect.height > 0 ? DISPLAY_HEIGHT / rect.height : 1;

      return {
        x: (event.clientX - (rect?.left ?? 0)) * scaleX,
        y: (event.clientY - (rect?.top ?? 0)) * scaleY,
      };
    }

    function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>): void {
      const canvas = canvasRef.current;

      if (canvas === null) return;

      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
        // jsdom / some browsers reject capture outside a real pointer session.
      }

      drawingRef.current = true;
      const point = pointFromEvent(event);

      lastPointRef.current = point;
      const context = canvas.getContext('2d');

      context?.beginPath();
      context?.moveTo(point.x, point.y);
    }

    function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>): void {
      if (!drawingRef.current) return;

      const canvas = canvasRef.current;
      const point = pointFromEvent(event);
      const last = lastPointRef.current;

      if (last !== null) {
        totalDistanceRef.current += Math.hypot(point.x - last.x, point.y - last.y);
      }

      const context = canvas?.getContext('2d');

      context?.lineTo(point.x, point.y);
      context?.stroke();
      lastPointRef.current = point;

      if (!hasStrokeRef.current && totalDistanceRef.current > STROKE_THRESHOLD_PX) {
        hasStrokeRef.current = true;
        onChange(true);
      }
    }

    function endStroke(event: ReactPointerEvent<HTMLCanvasElement>): void {
      drawingRef.current = false;
      lastPointRef.current = null;

      try {
        canvasRef.current?.releasePointerCapture(event.pointerId);
      } catch {
        // Same defensive catch as setPointerCapture above.
      }
    }

    useImperativeHandle(ref, () => ({
      clear() {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');

        if (canvas !== null && canvas !== undefined && context !== null && context !== undefined) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        }

        totalDistanceRef.current = 0;
        hasStrokeRef.current = false;
        onChange(false);
      },
      exportPng() {
        if (!hasStrokeRef.current) return null;

        return canvasRef.current?.toDataURL('image/png') ?? null;
      },
    }));

    return (
      <canvas
        ref={canvasRef}
        data-testid="signature-pad-canvas"
        aria-label="Área de firma"
        className="w-full touch-none rounded-md border border-slate-300 bg-white"
        style={{ height: DISPLAY_HEIGHT }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      />
    );
  },
);
